import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import PanoramaIcon from '@mui/icons-material/Panorama';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { TimelineClip } from '../../../types';

export interface DownloadClipFeedDialogProps {
  open: boolean;
  onClose: () => void;
  clip: TimelineClip | null;
  hasAuto: boolean;
  hasPano: boolean;
  onDownload: (feed: 'auto' | 'pano' | 'both') => Promise<void>;
}

const formatSeconds = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

export const DownloadClipFeedDialog: React.FC<DownloadClipFeedDialogProps> = ({
  open,
  onClose,
  clip,
  hasAuto,
  hasPano,
  onDownload,
}) => {
  const [selectedOption, setSelectedOption] = useState<'auto' | 'pano' | 'both'>('auto');
  const [isDownloading, setIsDownloading] = useState(false);

  if (!clip) return null;

  const handleConfirm = async () => {
    setIsDownloading(true);
    try {
      await onDownload(selectedOption);
      onClose();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isDownloading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#18181b',
          color: '#fff',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ContentCutIcon sx={{ color: '#38bdf8', fontSize: 20 }} />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem', color: '#fff' }}>
            Download Clip
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 2 }}>
        {/* Clip Summary Pill */}
        <Box
          sx={{
            p: 1.5,
            mb: 2.5,
            bgcolor: 'rgba(255, 255, 255, 0.04)',
            borderRadius: 1.5,
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff', mb: 0.5 }}>
            {clip.title || 'Custom Clip'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={`${formatSeconds(clip.startTime)} → ${formatSeconds(clip.endTime)}`}
              sx={{
                bgcolor: 'rgba(56, 189, 248, 0.15)',
                color: '#38bdf8',
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 22,
              }}
            />
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: 'monospace' }}>
              Duration: {(clip.duration ?? (clip.endTime - clip.startTime)).toFixed(1)}s
            </Typography>
          </Stack>
        </Box>

        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1.5, fontWeight: 600 }}>
          Select video feed angle to download:
        </Typography>

        <RadioGroup
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value as 'auto' | 'pano' | 'both')}
        >
          {/* Auto Feed Option */}
          <FormControlLabel
            value="auto"
            disabled={!hasAuto}
            control={<Radio sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#38bdf8' } }} />}
            label={
              <Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <AutoModeIcon sx={{ fontSize: 16, color: '#38bdf8' }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: hasAuto ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                    Auto Broadcast Feed
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                  Automated puck-tracking camera view
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5, alignItems: 'flex-start' }}
          />

          {/* Pano Feed Option */}
          <FormControlLabel
            value="pano"
            disabled={!hasPano}
            control={<Radio sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#38bdf8' } }} />}
            label={
              <Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PanoramaIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: hasPano ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                    Panoramic Feed {!hasPano && '(Unavailable)'}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                  Full-sheet static panoramic broadcast
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5, alignItems: 'flex-start' }}
          />

          {/* Both Feeds Option */}
          <FormControlLabel
            value="both"
            disabled={!hasAuto || !hasPano}
            control={<Radio sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#38bdf8' } }} />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: (hasAuto && hasPano) ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                  Download Both Feeds
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                  Exports separate MP4s for both broadcast angles
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Button onClick={onClose} disabled={isDownloading} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isDownloading}
          startIcon={isDownloading ? <CircularProgress size={16} sx={{ color: '#000' }} /> : <DownloadIcon />}
          sx={{
            bgcolor: '#38bdf8',
            color: '#000',
            fontWeight: 700,
            '&:hover': { bgcolor: '#7dd3fc' },
          }}
        >
          {isDownloading ? 'Preparing...' : 'Download MP4'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
